import { useEffect, useRef, useCallback } from 'react';
import { API_BASE } from './api';

/**
 * useServerEvents — connects to the SSE /events endpoint once.
 * Calls onInit(data) with the initial full dataset on connect.
 * Calls onUpdate(data) with only the changed slices when server pushes.
 * Automatically reconnects on disconnect with exponential backoff.
 * Never triggers a re-render by itself — callers decide what to do with data.
 */
export function useServerEvents({ onInit, onUpdate, onConnected, onError }) {
    const esRef      = useRef(null);
    const retryDelay = useRef(2000);
    const retryTimer = useRef(null);
    const mounted    = useRef(true);

    const connect = useCallback(() => {
        if (!mounted.current) return;
        if (esRef.current) {
            esRef.current.close();
            esRef.current = null;
        }

        const url = `${API_BASE}/events`;
        const es  = new EventSource(url, { withCredentials: true });
        esRef.current = es;

        es.addEventListener('connected', () => {
            retryDelay.current = 1000; // reset backoff on successful connect
            onConnected?.();
        });

        es.addEventListener('init', (e) => {
            try {
                const data = JSON.parse(e.data);
                onInit?.(data);
            } catch { /* malformed event */ }
        });

        es.addEventListener('update', (e) => {
            try {
                const data = JSON.parse(e.data);
                onUpdate?.(data);
            } catch { /* malformed event */ }
        });

        es.addEventListener('reconnect', () => {
            // Server asked us to reconnect cleanly (scheduled refresh)
            es.close();
            retryTimer.current = setTimeout(connect, 100);
        });

        es.onerror = () => {
            es.close();
            esRef.current = null;
            onError?.();
            if (!mounted.current) return;
            // Exponential backoff: 2s → 4s → 8s → max 60s
            retryTimer.current = setTimeout(() => {
                retryDelay.current = Math.min(retryDelay.current * 2, 60000);
                connect();
            }, retryDelay.current);
        };

    }, [onInit, onUpdate, onConnected, onError]);

    useEffect(() => {
        mounted.current = true;
        connect();
        return () => {
            mounted.current = false;
            if (retryTimer.current) clearTimeout(retryTimer.current);
            if (esRef.current) { esRef.current.close(); esRef.current = null; }
        };
    }, [connect]);
}